import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Loader2, Calendar, Check, RefreshCw, Utensils, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

/**
 * GenerateLactationMenuButton — calls GPT-5.2 with the user's
 * auto-detected perinatal ceiling + conditions, displays the resulting
 * 7-day rotation and offers a one-click "Add day to diary" action.
 */
export default function GenerateLactationMenuButton({ className = "" }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [loading, setLoading] = useState(false);
  const [menu, setMenu] = useState(null);
  const [ceiling, setCeiling] = useState(null);
  const [openDay, setOpenDay] = useState(0);
  const [addingDay, setAddingDay] = useState(null);

  const generate = async (force = false) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/perinatal-dil/menu/generate`, {
        days: 7,
        force_refresh: force,
      });
      setMenu(data.menu);
      setCeiling(data.ceiling);
      toast.success(
        data.cached
          ? (bg ? `Заредено от кеш (${data.age_hours}ч)` : `Loaded from cache (${data.age_hours}h)`)
          : (bg ? "Меню готово · 7 дни" : "Menu ready · 7 days"),
      );
    } catch (e) {
      const detail = e?.response?.data?.detail || (bg ? "Грешка при генериране" : "Generation failed");
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const addToDiary = async (dayIndex) => {
    setAddingDay(dayIndex);
    try {
      const { data } = await axios.post(`${API}/perinatal-dil/menu/add-day-to-diary`, {
        day_index: dayIndex,
      });
      toast.success(
        bg
          ? `Добавени ${data.inserted} ястия в дневника за ${data.date}`
          : `Added ${data.inserted} foods to diary for ${data.date}`,
      );
    } catch (e) {
      const detail = e?.response?.data?.detail || (bg ? "Грешка при добавяне" : "Add failed");
      toast.error(detail);
    } finally {
      setAddingDay(null);
    }
  };

  const days = menu?.days || [];

  return (
    <div className={className} data-testid="lactation-menu-generator">
      {!menu && (
        <button
          onClick={() => generate(false)}
          disabled={loading}
          className={`group w-full p-4 rounded-2xl border-2 border-dashed text-left transition-all ${
            isDark
              ? "border-pink-500/40 hover:border-pink-400 bg-pink-500/5 hover:bg-pink-500/10"
              : "border-pink-300 hover:border-pink-500 bg-pink-50 hover:bg-pink-100"
          } disabled:opacity-60`}
          data-testid="generate-menu-btn"
        >
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              isDark ? "bg-pink-500/30" : "bg-pink-200"
            }`}>
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin text-pink-600" />
              ) : (
                <Sparkles className="w-5 h-5 text-pink-600 dark:text-pink-300 group-hover:scale-110 transition-transform" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold">
                {bg ? "Генерирай 7-дневно IR-friendly меню за кърмене" : "Generate a 7-day IR-friendly lactation menu"}
              </div>
              <div className="text-xs opacity-70 mt-0.5">
                {bg
                  ? "GPT-5.2 ще създаде меню, което уважава Вашия личен DIL таван + всички диагностицирани състояния."
                  : "GPT-5.2 will build a menu that respects your personal DIL ceiling + every diagnosed condition."}
              </div>
            </div>
          </div>
        </button>
      )}

      {menu && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl p-4 ${
            isDark
              ? "bg-gradient-to-br from-pink-500/10 via-purple-500/8 to-rose-500/10 border border-pink-500/25"
              : "bg-gradient-to-br from-pink-50 via-purple-50 to-rose-50 border border-pink-200"
          }`}
          data-testid="lactation-menu-result"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider opacity-70">
                {bg ? "Седмично меню · GPT-5.2" : "Weekly menu · GPT-5.2"}
              </div>
              <div className="text-sm font-bold mt-0.5">
                {ceiling?.label_bg} · DIL ≤ {ceiling?.ceiling_g} g
              </div>
            </div>
            <button
              onClick={() => generate(true)}
              disabled={loading}
              className="text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-white/40 dark:hover:bg-white/5"
              data-testid="regenerate-menu-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              {bg ? "Прегенерирай" : "Regenerate"}
            </button>
          </div>

          {menu.summary_bg && (
            <p className="text-xs opacity-90 leading-relaxed mb-3 italic">
              {menu.summary_bg}
            </p>
          )}

          {/* Day list */}
          <div className="space-y-2">
            {days.map((day, i) => {
              const isOpen = openDay === i;
              return (
                <div
                  key={i}
                  className={`rounded-xl ${
                    isDark ? "bg-black/30" : "bg-white/70"
                  } overflow-hidden`}
                  data-testid={`menu-day-${i}`}
                >
                  <button
                    onClick={() => setOpenDay(isOpen ? -1 : i)}
                    className="w-full p-3 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 opacity-60" />
                      <span className="text-sm font-bold">{day.label || `${bg ? "Ден" : "Day"} ${day.day || i + 1}`}</span>
                      <span className="text-[11px] opacity-60">
                        · DIL <b>{day.total_dil_g}</b> g · {day.total_kcal} kcal
                      </span>
                    </div>
                    {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="px-3 pb-3 space-y-2">
                          {(day.meals || []).map((meal, mi) => (
                            <div
                              key={mi}
                              className={`p-2 rounded-lg ${isDark ? "bg-white/5" : "bg-pink-50/50"}`}
                            >
                              <div className="flex items-center gap-2 text-[11px] font-bold mb-1">
                                <Utensils className="w-3 h-3 text-pink-600" />
                                <span>{meal.time} · {meal.name}</span>
                                <span className="opacity-50">· DIL {meal.meal_dil_g} g</span>
                              </div>
                              <ul className="text-[11px] space-y-0.5 pl-4">
                                {(meal.items || []).map((it, ii) => (
                                  <li key={ii} className="list-disc">
                                    {it.food}{" "}
                                    <span className="opacity-50">
                                      (P{it.protein_g} · C{it.carbs_g} · DIL {it.dil_g})
                                    </span>
                                  </li>
                                ))}
                              </ul>
                              {meal.why_bg && (
                                <div className="text-[10px] opacity-70 mt-1 italic pl-4">💡 {meal.why_bg}</div>
                              )}
                            </div>
                          ))}

                          <button
                            onClick={() => addToDiary(i)}
                            disabled={addingDay === i}
                            className="w-full mt-2 px-3 py-2 rounded-lg bg-pink-600 hover:bg-pink-700 text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
                            data-testid={`add-day-${i}-to-diary`}
                          >
                            {addingDay === i ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Check className="w-3.5 h-3.5" />
                            )}
                            {bg ? "Добави този ден в дневника" : "Add this day to my diary"}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
}
